import { Element, Prop, Component, Host, h, Event, EventEmitter } from '@stencil/core';

export interface AhaSettingsPopover extends HTMLAhaSettingsPopoverElement {}

/**
 * `aha-settings-popover` is used to provide additional documentation for the surrounding context.
 *
 * The popover opens down and to the left, this component should be positioned in the upper right in any context.
 *
 * @slot heading - Override the contents of the popover heading, prefer using the heading property instead.
 * @slot trigger - Override the contents of the popover trigger.
 * @slot default - The body of the help documentation. This content should answer the question posed by the heading. For complex features, consider ending the content with a link to more thorough documentation.
 */
@Component({
  tag: 'aha-settings-popover',
  styleUrl: 'aha-settings-popover.css',
  shadow: true,
})
export class AhaSettingsPopover {
  /**
   * Deprecated, use heading slot instead.
   */
  @Prop() heading: string = '';

  /**
   * Controls whether the popover is open or closed. The component will manage this property when clicked.
   * `is-open` can be used used to override control.
   */
  @Prop({ reflect: true }) isOpen = false;

  /**
   * Sets the width of the opened popover contents.
   */
  @Prop() openWidth: string = '300px';

  @Element() el!: HTMLAhaSettingsPopoverElement;

  private closeIcon?: HTMLFaIconElement;

  /**
   * Triggers when aha-settings-popover is initially opened, but before it is visible.
   */
  @Event({ eventName: 'aha-settings-popover:open', cancelable: true, bubbles: true }) //eslint-disable-line @stencil/decorators-style
  popoverOpen!: EventEmitter;

  /**
   * Triggers when aha-settings-popover is visible
   */
  @Event({ eventName: 'aha-settings-popover:opened', cancelable: true, bubbles: true }) //eslint-disable-line @stencil/decorators-style
  popoverOpened!: EventEmitter;

  /**
   * Triggers when aha-settings-popover is initially closed, but before it is not visible.
   */
  @Event({ eventName: 'aha-settings-popover:close', cancelable: true, bubbles: true }) //eslint-disable-line @stencil/decorators-style
  popoverClose!: EventEmitter;

  /**
   * Triggers when aha-settings-popover is not visible.
   */
  @Event({ eventName: 'aha-settings-popover:closed', cancelable: true, bubbles: true }) //eslint-disable-line @stencil/decorators-style
  popoverClosed!: EventEmitter;

  private handleOpenActivation = (newIsOpen: boolean) => (event: Event) => {
    const allowedEvents = ['click'];
    if (Object.values(allowedEvents).includes(event.type)) {
      newIsOpen ? this.popoverOpen.emit() : this.popoverClose.emit();
      this.isOpen = newIsOpen;
      newIsOpen ? this.popoverOpened.emit() : this.popoverClosed.emit();
    } else if (event instanceof KeyboardEvent) {
      switch (event.type) {
        case 'keyup': {
          if (event.key === ' ') {
            event.preventDefault();
            this.isOpen = newIsOpen;
          }
        }
        case 'keydown': {
          if (event.key === ' ') {
            event.preventDefault();
          } else if (event.key === 'Enter') {
            event.preventDefault();
            this.isOpen = newIsOpen;
          }
        }
      }
    }

    if (newIsOpen && this.isOpen) {
      this.closeIcon?.focus();
    }
  };

  render() {
    let offset = '0px';
    if (this.isOpen) {
      offset = (window.getComputedStyle(this.el) as { [key: string]: any })!['padding-right'];
    }

    return (
      <Host>
        <span class="aha-settings-popover__wrapper">
          <div
            class="aha-settings-popover__button"
            aria-label={`${this.heading || 'More information'}`}
            tabindex="0"
            onClick={this.handleOpenActivation(true)}
            onKeyDown={this.handleOpenActivation(true)}
            onKeyUp={this.handleOpenActivation(true)}
            title={this.heading}
          >
            <slot name="trigger">
              <fa-icon class="fa-regular fa-circle-info aha-settings-popover__icon"></fa-icon>
              Info
            </slot>
          </div>
          {this.isOpen && (
            <span
              aria-labelledby="aha-settings-popover__heading"
              class="aha-settings-popover"
              style={{ right: offset, width: this.openWidth }}
              role="dialog"
            >
              <fa-icon
                ref={el => (this.closeIcon = el)}
                aria-label="Close"
                tabindex="0"
                role="button"
                class="fa-regular fa-circle-xmark aha-settings-popover__close"
                onClick={this.handleOpenActivation(false)}
                onKeyDown={this.handleOpenActivation(false)}
                onKeyUp={this.handleOpenActivation(false)}
              ></fa-icon>
              <header id="aha-settings-popover__heading" class="aha-settings-popover__heading">
                <slot name="heading">{this.heading}</slot>
              </header>
              <div class="aha-settings-popover__content">
                <slot></slot>
              </div>
            </span>
          )}
        </span>
      </Host>
    );
  }
}
